import { useLocale, useTranslations } from "use-intl";
import { useNavigate } from "react-router";
import { useState, useTransition } from "react";
import { FilePlus2, FileUp, Loader2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { createPlan, importPlanFile } from "@/lib/actions";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";

export function NewPlanButton({ templates }: { templates: { key: string; name: string; description: string }[] }) {
  const t = useTranslations("plans");
  const tc = useTranslations("common");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pending, start] = useTransition();
  const locale = useLocale();
  const navigate = useNavigate();
  const create = (input: { template?: string; name?: string }) =>
    start(async () => {
      const { id } = await createPlan({ ...input, locale });
      setOpen(false);
      navigate(`/plans/${id}`);
    });
  /** A plan file from "Share → Download file" (validated by the server). */
  const importFile = (file: File) =>
    start(async () => {
      try {
        const { id } = await importPlanFile(JSON.parse(await file.text()));
        setOpen(false);
        toast.success(t("imported"));
        navigate(`/plans/${id}`);
      } catch {
        toast.error(t("importFailed"));
      }
    });

  return (
    <>
      <Button size="lg" className="rounded-full" onClick={() => setOpen(true)}>
        <Plus /> {t("newPlan")}
      </Button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{t("newPlanTitle")}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-3 px-4 pb-8">
            {templates.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                disabled={pending}
                onClick={() => create({ template: tpl.key })}
                className="flex w-full gap-3 rounded-2xl border p-4 text-left hover:bg-muted/50"
              >
                <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" />
                <span>
                  <span className="block text-xs font-medium uppercase text-muted-foreground">{t("fromTemplate")}</span>
                  <span className="block font-semibold">{tpl.name}</span>
                  <span className="mt-0.5 line-clamp-2 block text-sm text-muted-foreground">{tpl.description}</span>
                </span>
              </button>
            ))}
            <form
              className="rounded-2xl border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                create({ name });
              }}
            >
              <div className="flex gap-3">
                <FilePlus2 className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <p className="font-semibold">{t("blank")}</p>
                  <p className="text-sm text-muted-foreground">{t("blankText")}</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("planName")}
                  required
                  className="h-11 text-base"
                />
                <Button type="submit" size="lg" className="h-11" disabled={pending || !name.trim()}>
                  {pending ? <Loader2 className="animate-spin" /> : tc("create")}
                </Button>
              </div>
            </form>
            <label className="flex w-full cursor-pointer gap-3 rounded-2xl border p-4 text-left hover:bg-muted/50 has-[:disabled]:opacity-50">
              <FileUp className="mt-0.5 size-5 shrink-0 text-primary" />
              <span>
                <span className="block font-semibold">{t("importFile")}</span>
                <span className="mt-0.5 block text-sm text-muted-foreground">{t("importFileText")}</span>
              </span>
              <input
                type="file"
                accept=".json,application/json"
                className="sr-only"
                disabled={pending}
                onChange={(e) => {
                  const file = e.currentTarget.files?.[0];
                  e.currentTarget.value = "";
                  if (file) importFile(file);
                }}
              />
            </label>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
